import React from "react";
import { ModalDialogBase, ModalDialogBaseProps } from "./ModalDialogBase";
import { ModalDialogConfirmButtons, ProcessStatusPanel } from "components";
import { ProcessState } from "types/process";
import { IsRenderableReactNode } from "modules/utility/utility";

export interface ModalDialogConfirmStatusProps
  extends Omit<ModalDialogBaseProps, "actionsSection"> {
  //Content section props
  hideContentOnProcessComplete?: boolean; //When process complete, hide or show content section

  //Action section props
  defaultButton?: "OK" | "CANCEL";
  cancelButtonText?: string;
  cancelButtonDisabled?: boolean;
  onCancelButtonClick?: () => void;
  okButtonText?: string;
  okButtonDisabled?: boolean;
  okButtonWarningColor?: boolean;
  onOKButtonClick?: () => void;

  //Status section props
  processState: ProcessState;
  percentComplete?: number;
  statusMessage?: React.ReactNode;
  inProgressTitle?: string;
  successTitle?: string;
  errorTitle?: string;

  //Result section props
  resultSection?: string | React.ReactNode;
}

export const ModalDialogConfirmStatus: React.FC<
  ModalDialogConfirmStatusProps
> = ({
  //Dialog Props
  open,
  fullWidth = false,
  maxWidth = false,
  forceOnTop = false,

  title = "",
  onClose,
  disableEscapeKeyDown = false,
  disableCloseOnBackdropClick = false,

  //Content section props
  contentSection,
  hideContentOnProcessComplete = false,

  //Action button props
  defaultButton = "CANCEL",
  cancelButtonText = "Cancel",
  cancelButtonDisabled = false,
  onCancelButtonClick,
  okButtonText = "OK",
  okButtonDisabled = false,
  okButtonWarningColor = false,
  onOKButtonClick,

  //Status section props
  processState,
  percentComplete,
  statusMessage: message,
  inProgressTitle,
  successTitle,
  errorTitle,

  //Results section props
  resultSection,
}) => {
  //Provide default handler to close icon of the cancel button
  const handleCancelButtonClick = onCancelButtonClick ?? onClose ?? undefined;

  //Get shortcuts for knowing what the state of the process is
  const processIsReady = processState === ProcessState.IDLE;
  const processIsBusy =
    processState === ProcessState.INITIALIZING ||
    processState === ProcessState.QUEUED ||
    processState === ProcessState.PROCESSING;
  const processIsDone =
    processState === ProcessState.COMPLETE ||
    processState === ProcessState.ERROR;

  // Decide if we should show content section
  // We will always show content section EXCEPT when:
  // 1) There is no content OR
  // 2) The processing has been done,
  //    AND we are flagged to hide content when process is done
  const hasContent = IsRenderableReactNode(contentSection);
  const shouldShowContent =
    hasContent && !(processIsDone && hideContentOnProcessComplete);

  // Build the Action Buttons section
  const actionsSection = React.useMemo<React.ReactNode>(() => {
    // If the process is ready to run,show the default set of buttons
    if (processIsReady) {
      return (
        <ModalDialogConfirmButtons
          defaultButton={defaultButton}
          cancelButtonText={cancelButtonText}
          cancelButtonDisabled={cancelButtonDisabled}
          cancelButtonVisible={true}
          onCancelButtonClick={handleCancelButtonClick}
          okButtonText={okButtonText}
          okButtonDisabled={okButtonDisabled}
          okButtonVisible={true}
          okButtonWarningColor={okButtonWarningColor}
          onOKButtonClick={onOKButtonClick}
        />
      );
    }

    // If the process is done, show just a CLOSE button
    if (processIsDone) {
      return (
        <ModalDialogConfirmButtons
          defaultButton="CANCEL"
          cancelButtonText="CLOSE"
          cancelButtonDisabled={false}
          cancelButtonVisible={true}
          onCancelButtonClick={handleCancelButtonClick}
          okButtonVisible={false}
        />
      );
    }

    return null;
  }, [
    processIsReady,
    processIsDone,
    defaultButton,
    cancelButtonText,
    cancelButtonDisabled,
    okButtonText,
    okButtonDisabled,
    okButtonWarningColor,
    onOKButtonClick,
    handleCancelButtonClick,
  ]);

  // Decide if the status section should be shown.
  // Should not be shown if the process hasn't started yet.
  const shouldShowStatus = processIsBusy || processIsDone;
  const statusSection = React.useMemo<React.ReactNode>(() => {
    if (!shouldShowStatus) return undefined;

    return (
      <ProcessStatusPanel
        processState={processState}
        percentComplete={percentComplete}
        message={message}
        inProgressTitle={inProgressTitle}
        successTitle={successTitle}
        errorTitle={errorTitle}
      />
    );
  }, [
    shouldShowStatus,
    processState,
    percentComplete,
    message,
    inProgressTitle,
    successTitle,
    errorTitle,
  ]);

  return (
    <ModalDialogBase
      open={open}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      forceOnTop={forceOnTop}
      title={title}
      onClose={onClose}
      disableEscapeKeyDown={disableEscapeKeyDown}
      disableCloseOnBackdropClick={disableCloseOnBackdropClick}
      contentSection={shouldShowContent ? contentSection : undefined}
      actionsSection={actionsSection}
      statusSection={statusSection}
      resultSection={resultSection}
    />
  );
};
